import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { convertISO8601ToDuration, convertTimeToArabic, isActionRunSuccessfully, isEmpty } from "../../../Lib/Utils/validationUtil";
import { srReqClaimTree } from "../../../Orchestrator/ConstantTree/ServiceRequestClaim/ServiceRequestClaim";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookingResponse, IBookingTypeTrip } from "../../../Orchestrator/Types/Index";
import { IGetBookingDetailsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import { TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { getBookingDetailStatusAction } from "../../UIActions/BookingDetail/GetBookingDetailStatusAction";
import { getLegsSummaryFromTripAction } from "../../UIActions/TrainOffer/GetLegsFromTripAction";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { preferTrainTrips } from "../../../Lib/Utils/booking";

class GetBookingTripDetailsAction extends BaseAction<void, IGetBookingDetailsActionParams> {

    override async run(): Promise<void> {
        try {
            const { bookingId, direction, screen = "serviceRequest" } = this.params!;
            if (bookingId) {
                const response: any = await getBooking(bookingId);
                if (response.status === 'ok' && response.answer) {
                    let bookingResponse: IBookingResponse = response.answer;
                    const { booking } = bookingResponse;

                    updateRecoilState("bookingResponseAtom", bookingResponse);

                    let allBookedOfferWithoutExchange: IBookedOffer[] = []
                    let allTripWithoutExchange: IBookingTypeTrip[] = []
                    let outboundTrip: IBookingTypeTrip | undefined = undefined
                    let inboundTrip: IBookingTypeTrip | undefined = undefined
                    let legSummaryResult: any = null;

                    getAllOfferWithoutExchangeAction.setParams(booking.bookedOffers)
                    await getAllOfferWithoutExchangeAction.execute()
                    if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
                        allBookedOfferWithoutExchange = getAllOfferWithoutExchangeAction?.result || []
                        if (allBookedOfferWithoutExchange.length > 0) {
                            getAllTripWithoutExchangeAction.setParams({
                                bookedOffers: allBookedOfferWithoutExchange,
                                trips: booking.trips
                            })
                            await getAllTripWithoutExchangeAction.execute();
                            if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
                                allTripWithoutExchange = getAllTripWithoutExchangeAction?.result || [];
                            }
                        }
                    }

                    let needToTakeOutbound = false
                    let needToTakeInbound = false
                    if (direction === TrainTypeEnum.outbound) needToTakeOutbound = true
                    if (direction === TrainTypeEnum.inbound) needToTakeInbound = true
                    else {
                        if (direction === "") {
                            needToTakeOutbound = true
                            needToTakeInbound = true
                        }
                    }

                    if (allBookedOfferWithoutExchange.length > 0 && allTripWithoutExchange.length > 0) {
                        // Prefer trips with a TRAIN leg — a standalone onward-shuttle
                        // trip can share the same direction as the train trip and must
                        // not hijack this lookup (see preferTrainTrips doc).
                        outboundTrip = preferTrainTrips(allTripWithoutExchange).find((trip) => trip.direction === TripTypeEnum.OUT_BOUND)
                        inboundTrip = preferTrainTrips(allTripWithoutExchange).find((trip) => trip.direction === TripTypeEnum.IN_BOUND)
                        if (outboundTrip && needToTakeOutbound) {
                            const bookedTrip: Partial<TrainType> = {
                                id: bookingResponse.booking.id,
                                departureTime: convertTimeToArabic(outboundTrip.startTime),
                                arrivalTime: convertTimeToArabic(outboundTrip.endTime),
                                duration: convertISO8601ToDuration(outboundTrip.duration),
                                trainNo: outboundTrip?.legs[0]?.timedLeg?.service?.vehicleNumbers?.[0] || '',
                                bookingCode: booking.bookingCode || '',
                                legs: outboundTrip.legs
                            }
                            updateRecoilState("selectedTrainStateAtom", bookedTrip)
                            getLegsSummaryFromTripAction.setParams({
                                trip: { legs: bookedTrip.legs, priceClass: "" },
                                tripDirection: TrainTypeEnum.outbound
                            })
                            await getLegsSummaryFromTripAction.execute();
                            if (isActionRunSuccessfully(getLegsSummaryFromTripAction)) {
                                legSummaryResult = getLegsSummaryFromTripAction.result;
                            }
                        } else updateRecoilState("selectedTrainStateAtom", null)
                        if (inboundTrip && needToTakeInbound) {
                            const bookedTrip: Partial<TrainType> = {
                                id: bookingResponse.booking.id,
                                departureTime: convertTimeToArabic(inboundTrip.startTime),
                                arrivalTime: convertTimeToArabic(inboundTrip.endTime),
                                duration: convertISO8601ToDuration(inboundTrip.duration),
                                trainNo: inboundTrip?.legs[0]?.timedLeg?.service?.vehicleNumbers?.[0] || '',
                                bookingCode: booking.bookingCode || '',
                                legs: inboundTrip.legs,
                            }
                            updateRecoilState("selectedInboundTrainStateAtom", bookedTrip)
                            getLegsSummaryFromTripAction.setParams({
                                trip: { legs: bookedTrip.legs, priceClass: "" },
                                tripDirection: TrainTypeEnum.inbound,
                                trainLegsValue: legSummaryResult
                            })
                            await getLegsSummaryFromTripAction.execute();
                        } else updateRecoilState("selectedInboundTrainStateAtom", null)
                    }

                    if (screen === "serviceRequest") {
                        const serviceRequestTrip: string[] = []
                        if (needToTakeOutbound && outboundTrip) serviceRequestTrip.push(TrainTypeEnum.outbound)
                        if (needToTakeInbound && inboundTrip) serviceRequestTrip.push(TrainTypeEnum.inbound)
                        updateRecoilState("selectedTripForServiceRequestAtom", serviceRequestTrip)
                    } else {
                        updateRecoilState("selectedTripForServiceRequestAtom", [])
                    }

                    getBookingDetailStatusAction.setParams({ booking: bookingResponse });
                    await getBookingDetailStatusAction.execute();
                    const result = getBookingDetailStatusAction?.result?.data || ""
                    if (result !== "") {
                        let context = "CONFIRMED"
                        if (result === "PAST") context = "PAST"
                        else if (result === "FULFILLED") context = "CONFIRMED"
                        else if (result === "REFUNDED") context = "CANCELLED"                        
                        _eventBus.emit({
                            eventName: srReqClaimTree.Category.Get,
                            data: context,
                        });
                    }
                }
            } else {
                console.warn("Failed to fetch Data.");
            }
        } catch (error: any) {
            console.error("Error fetching Data:", error);
        }
    }
}

export const getBookingTripDetailsAction = new GetBookingTripDetailsAction()